import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setSelectedGameId } from '../../redux/slice/game.slice';
import { getGamePath } from '../../utils/gamePath';
import { addToCart } from '../../redux/slice/user.slice';
import { Timer, Zap, Award, ShieldCheck } from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import { fetchDealsBanner } from '../../redux/slice/discount.slice';
import gsap from 'gsap';
import toast from 'react-hot-toast';

const PlatformIcons = {
  windows: FaWindows,
  playstation: SiPlaystation,
  xbox: FaXbox,
  nintendo: BsNintendoSwitch,
  quest: SiOculus,
  android: DiAndroid,
  ios: FaApple,
  visionpro: TbDeviceVisionProFilled,
  steamdeck: SiSteamdeck
};

const PlatformColors = {
  windows: '#0078d7',
  playstation: '#00439c',
  xbox: '#107c10',
  nintendo: '#e60012',
  quest: '#ffffff',
  android: '#3ddc84',
  ios: '#a3aaae',
  visionpro: '#a855f7',
  steamdeck: '#38bdf8'
};

const PlatformLabels = {
  windows: 'Windows PC',
  playstation: 'PlayStation',
  xbox: 'Xbox',
  nintendo: 'Nintendo Switch',
  quest: 'Meta Quest VR',
  android: 'Android',
  ios: 'iOS',
  visionpro: 'Vision Pro',
  steamdeck: 'Steam Deck'
};

export function DealsSectionSkeleton() {
  return (
    <section id="deals-section" className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden animate-pulse">
      {/* Background orbs */}
      <div className="absolute top-[20%] left-[-10%] w-[350px] h-[350px] bg-cyber-pink/5 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto relative z-10">
        {/* Header Skeleton */}
        <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center mb-6 sm:mb-8">
          <div className="h-4 w-36 rounded-full bg-cyan-400/20 border border-cyan-400/30 mb-3" />
          <div className="h-8 sm:h-10 lg:h-12 w-56 sm:w-72 rounded-xl bg-white/10" />
          <div className="h-[2px] w-20 bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-cyan mt-3 rounded-full mx-auto opacity-40" />
          <div className="h-3.5 w-full max-w-md bg-white/5 rounded mt-4" />
        </div>

        {/* Large Cyber Banner Card Skeleton */}
        <div className="relative rounded-2xl p-6 sm:p-10 lg:p-12 overflow-hidden flex flex-col lg:flex-row gap-10 items-center justify-between bg-[#0b0618]/90 border border-white/10 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)]">
          <div className="flex-1 space-y-6 w-full">
            {/* Tag Pill */}
            <div className="h-6 w-44 rounded-full bg-white/10 border border-white/15" />

            {/* Title Skeleton */}
            <div className="space-y-2">
              <div className="h-4 w-32 bg-cyber-pink/20 rounded" />
              <div className="h-8 sm:h-10 w-3/4 bg-white/10 rounded-xl" />
            </div>

            {/* Description Skeleton */}
            <div className="space-y-2 max-w-lg hidden sm:block">
              <div className="h-3.5 w-full bg-white/5 rounded" />
              <div className="h-3.5 w-4/5 bg-white/5 rounded" />
            </div>

            {/* Platforms Bar Skeleton */}
            <div className="flex items-center gap-2">
              <div className="h-3 w-20 bg-white/10 rounded" />
              <div className="h-7 w-7 rounded-lg bg-white/5 border border-white/10" />
              <div className="h-7 w-7 rounded-lg bg-white/5 border border-white/10" />
              <div className="h-7 w-7 rounded-lg bg-white/5 border border-white/10" />
            </div>

            {/* Price Box Skeleton */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              <div className="h-10 w-24 rounded-xl bg-white/10" />
              <div className="h-10 w-32 rounded-xl bg-white/15" />
              <div className="h-8 w-24 rounded-xl bg-cyber-pink/20 border border-cyber-pink/30" />
            </div>

            {/* CTAs Skeleton */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
              <div className="h-12 w-full sm:w-48 rounded-xl bg-gradient-to-r from-cyber-pink/30 to-cyber-purple/30 border border-cyber-pink/40" />
              <div className="h-12 w-full sm:w-36 rounded-xl bg-white/5 border border-white/10" />
            </div>
          </div>

          {/* Timer Block Skeleton */}
          <div className="w-full lg:w-auto flex flex-col items-center justify-center space-y-6">
            <div className="border border-white/10 p-5 rounded-2xl flex flex-col items-center space-y-3 w-full sm:w-auto bg-black/40">
              <div className="h-4 w-44 bg-cyan-400/20 rounded" />
              <div className="flex items-center gap-2 sm:gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-xl bg-cyber-black border border-cyber-blue/20" />
                    <div className="h-2.5 w-8 bg-white/10 rounded mt-1.5" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DealsSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { games } = useSelector((s) => s.game);
  const { dealsBanner, loading: discountLoading } = useSelector((s) => s.discount);

  const bannerRef = useRef(null);

  // Countdown timer logic
  const calculateTimeLeft = (endTime) => {
    let targetDate;
    if (endTime) {
      targetDate = new Date(endTime);
    } else {
      // Default fallback: 5 days from now
      targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + 5);
      targetDate.setHours(23, 59, 59);
    }

    const difference = +targetDate - +new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    } else {
      timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    return timeLeft;
  };

  const isUpcoming = dealsBanner ? new Date(dealsBanner.startDate) > new Date() : false;
  const targetTime = isUpcoming ? dealsBanner?.startDate : dealsBanner?.endDate;

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetTime));

  useEffect(() => {
    dispatch(fetchDealsBanner());
  }, [dispatch]);

  useEffect(() => {
    if (!targetTime) return;
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetTime));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetTime]);

  const { loading: homeLoading } = useSelector((s) => s.home || {});

  if (discountLoading || homeLoading || !dealsBanner) {
    if (!discountLoading && !homeLoading && !dealsBanner) {
      return null;
    }
    return <DealsSectionSkeleton />;
  }

  const displayData = {
    id: dealsBanner.game._id,
    slug: dealsBanner.game.slug,
    title: dealsBanner.title || dealsBanner.game.title,
    subtitle: dealsBanner.subtitle || "LIMITED TIME GAMING OFFER",
    description: dealsBanner.description || dealsBanner.game.tagline || dealsBanner.game.description || "Experience the absolute pinnacle of high-fidelity VR speed.",
    discount: dealsBanner.discount,
    discountType: dealsBanner.discountType,
    startDate: dealsBanner.startDate,
    endDate: dealsBanner.endDate,
    originalPrice: dealsBanner.originalPrice,
    offerPrice: dealsBanner.offerPrice,
    bgImage: dealsBanner.game.cover_image?.url || dealsBanner.game.images?.[0]?.url,
    code: dealsBanner.code,
    method: dealsBanner.method,
    platforms: dealsBanner.platforms
  };

  // GSAP Hover and Glow animations
  const handleMouseEnter = () => {
    gsap.to(bannerRef.current, {
      scale: 1.015,
      // boxShadow: '0 0 35px rgba(189, 0, 255, 0.4), 0 0 15px rgba(0, 240, 255, 0.2)',
      // borderColor: 'rgba(189, 0, 255, 0.4)',
      duration: 0.3,
      ease: "power2.out"
    });
    gsap.to(".deal-image", {
      scale: 1.08,
      duration: 0.6,
      ease: "power2.out"
    });
  };

  const handleMouseLeave = () => {
    gsap.to(bannerRef.current, {
      scale: 1,
      boxShadow: '0 10px 30px -10px rgba(0,0,0,0.8)',
      borderColor: 'rgba(255, 255, 255, 0.06)',
      duration: 0.4,
      ease: "power3.out"
    });
    gsap.to(".deal-image", {
      scale: 1.04,
      duration: 0.5,
      ease: "power3.out"
    });
  };

  // Double digit pad helper
  const padZero = (num) => String(num).padStart(2, '0');

  return (
    <section id="deals-section" className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background orbs */}
      <div className="absolute top-[20%] left-[-10%] w-[350px] h-[350px] bg-cyber-pink/5 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto relative z-10">
        <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center mb-6 sm:mb-8">
          <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2 justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
            Limited Time Flash Sale
          </p>
          <h2 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-normal break-words">
            Flash <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Deals</span>
          </h2>
          <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full mx-auto"></div>
          <p className="text-gray-400 font-sans max-w-lg text-xs sm:text-sm mx-auto mt-3.5 leading-relaxed">
            Grab limited-quantity verified keys at massive discounts before time runs out.
          </p>
        </div>

        {/* Large RGB Interactive Sale Banner */}
        <div
          ref={bannerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            backgroundImage: displayData.bgImage ? `url(${displayData.bgImage})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          className="relative rounded-2xl p-2 py-6 sm:p-10 lg:p-12 overflow-hidden flex flex-col lg:flex-row gap-10 items-center justify-between transition-all duration-500 transform-gpu"
        >
          {/* Dark Overlay for background image */}
          {displayData.bgImage && (
            <div className="absolute inset-0 bg-black/80  z-0"></div>
          )}
          {/* Running RGB Cyber Trim */}
          <div className="absolute -bottom-0 left-0 h-[3px] bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-blue w-full animate-pulse-slow"></div>

          {/* Left: Content and Title */}
          <div className="flex-1 space-y-6 relative z-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center mx-auto lg:mr-auto lg:ml-0 space-x-1 px-3 py-1 rounded-full border  text-white text-[10px] font-cyber tracking-widest uppercase">
                <Zap className="w-3.5 h-3.5 mr-1 text-white animate-bounce" />
                LIMITED QUANTITY AVAILABLE
              </span>
              {displayData.method === 'coupon' && displayData.code && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(displayData.code);
                    // alert(`Coupon code "${displayData.code}" copied to clipboard!`);
                    toast.success(`Code copied! : ${displayData.code}`)
                  }}
                  className="flex items-center mx-auto lg:mr-auto lg:ml-0 space-x-1.5 px-3 py-1 rounded-full border border-cyber-pink bg-cyber-pink/20 hover:bg-cyber-pink/30 text-cyber-pink text-[10px] font-cyber tracking-widest uppercase font-bold cursor-pointer transition-all duration-200"
                  title="Click to copy coupon code"
                >
                  <span>COUPON CODE: {displayData.code} (CLICK TO COPY)</span>
                </button>
              )}
            </div>

            <div className="space-y-2 lg:text-start text-center">
              <span className="text-cyber-pink md:block hidden font-cyber text-[10px] font-black tracking-[0.3em] uppercase mb-1">
                {displayData.subtitle}
              </span>
              <h3 className="font-cyber font-black text-2xl sm:text-3xl md:text-4xl tracking-tight leading-[1.1] text-white uppercase">
                {displayData.title.split(' ').slice(0, -1).join(' ')} <br className="sm:hidden" />
                <span className="bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-cyan bg-clip-text text-transparent filter drop-shadow-[0_0_15px_rgba(255,0,122,0.3)]">
                  {displayData.title.split(' ').slice(-1)}
                </span>
              </h3>
            </div>

            <p className="text-gray-300 md:block hidden text-sm max-w-lg leading-relaxed font-sans lg:text-start text-center">
              {displayData.description}
            </p>

            {/* Platform Icons */}
            {displayData.platforms && (
              <div className="flex items-center justify-center lg:justify-start space-x-2.5">
                <span className="text-[10px] text-gray-400 font-cyber mr-1 uppercase">Available on:</span>
                {Object.keys(displayData.platforms).map(plat => {
                  if (!displayData.platforms[plat]?.available) return null;
                  const IconComponent = PlatformIcons[plat] || FaWindows;
                  const brandColor = PlatformColors[plat] || '#9ca3af';
                  const label = PlatformLabels[plat] || plat;
                  return (
                    <div 
                      key={plat} 
                      className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/[0.08] transition-all duration-200"
                      style={{ borderColor: `${brandColor}30`, color: brandColor }}
                      title={label}
                    >
                      <IconComponent className="w-4 h-4" />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Price tag layout */}
            <div className="flex flex-wrap items-center justify-evenly lg:justify-start gap-2 md:gap-8 ">
              <div className="flex flex-col items-center sm:items-start">
                <span className="text-[10px] text-gray-400 font-cyber">ORIGINAL PRICE</span>
                <span className="text-sm sm:text-lg text-gray-400 line-through font-cyber">${displayData.originalPrice.toFixed(2)}</span>
              </div>
              <div className="flex flex-col items-center sm:items-start">
                <span className="text-[10px] text-cyber-pink font-cyber">OFFER PRICE</span>
                <span className="text-2xl sm:text-4xl font-cyber font-black text-white text-glow-pink">${displayData.offerPrice.toFixed(2)}</span>
              </div>
              <div className="px-4 py-2 bg-cyber-pink/20 border border-cyber-pink/40 text-cyber-pink font-cyber font-black text-xs sm:text-base rounded-xl shadow-cyber-pink/20 shadow-[0_0_12px_rgba(255,0,122,0.2)]">
                -{displayData.discountType === 'percentage' ? `${displayData.discount}%` : `$${displayData.discount}`} OFF
              </div>
            </div>

            {/* Quick CTAs */}
            <div className="flex flex-col md:flex-row items-center gap-4 pt-2">
              <button
                onClick={() => {
                  const gameToAdd = dealsBanner ? {
                    ...dealsBanner.game,
                    platforms: dealsBanner.platforms
                  } : {
                    _id: 3,
                    title: "CYBERPUNK NEXUS: 2088",
                    platforms: { windows: { price: 39.99, available: true } }
                  };

                  const availablePlats = dealsBanner?.platforms ? Object.keys(dealsBanner.platforms).filter(
                    plat => dealsBanner.platforms[plat].available
                  ) : [];
                  const selectedPlatform = availablePlats.length > 0 ? availablePlats[0] : 'windows';
                  gameToAdd.selectedPlatform = selectedPlatform;

                  const discountPercent = displayData.discountType === 'percentage'
                    ? displayData.discount
                    : Math.round(((displayData.originalPrice - displayData.offerPrice) / displayData.originalPrice) * 100);

                  gameToAdd.discount = discountPercent;
                  gameToAdd.price = displayData.offerPrice;
                  gameToAdd.originalPrice = displayData.originalPrice;

                  if (gameToAdd.platforms) {
                    gameToAdd.platforms = { ...gameToAdd.platforms };
                    Object.keys(gameToAdd.platforms).forEach(plat => {
                      gameToAdd.platforms[plat] = {
                        ...gameToAdd.platforms[plat],
                        price: displayData.offerPrice,
                        originalPrice: gameToAdd.platforms[plat].price !== undefined ? gameToAdd.platforms[plat].price : displayData.originalPrice
                      };
                    });
                  }

                  dispatch(addToCart(gameToAdd));
                }}
                className="cyber-btn w-full lg:w-auto !px-2  !py-3.5 flex items-center justify-center text-center"
              >
                BUY NOW - SAVE {displayData.discountType === 'percentage' ? `${displayData.discount}%` : `$${displayData.discount}`}
              </button>
              <button
                onClick={() => {
                  dispatch(setSelectedGameId(displayData.id));
                  navigate(getGamePath(displayData));
                }}
                className="cyber-btn w-full lg:w-auto !px-2 !py-3.5 !bg-transparent !border-white/10 hover:!border-cyber-pink/40 flex items-center justify-center text-center"
              >
                VIEW DETAILS
              </button>
            </div>
          </div>

          {/* Right: Timer block and visuals */}
          <div className="w-full lg:w-auto flex flex-col items-center justify-center space-y-6 relative z-10">
            {/* Countdown block */}
            <div className="glass-panel border-white/10 p-5 lg:p-5 rounded-2xl flex flex-col items-center space-y-2 w-full sm:w-auto overflow-hidden">
              <div className="flex items-center space-x-2 text-cyber-cyan text-[10px] sm:text-xs font-cyber font-bold tracking-widest text-center">
                <Timer className="w-3.5 h-3.5 text-cyber-cyan animate-pulse" />
                <span>{isUpcoming ? "UPCOMING DEAL - STARTS IN" : "LIMITED TIME REMAINING"}</span>
              </div>

              {/* Countdown Numbers */}
              <div className="flex items-center gap-2 sm:gap-4 text-center">

                {/* Days */}
                <div className="flex flex-col">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg lg:rounded-xl bg-cyber-black border border-cyber-blue/20 flex items-center justify-center text-sm sm:text-lg lg:text-2xl  font-cyber font-black text-cyber-blue text-glow-cyan">
                    {padZero(timeLeft.days || 0)}
                  </div>
                  <span className="text-[8px] sm:text-[9px] text-gray-500 font-cyber font-bold mt-1.5 uppercase">Days</span>
                </div>

                <span className="text-sm sm:text-xl font-bold text-gray-600">:</span>

                {/* Hours */}
                <div className="flex flex-col">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg lg:rounded-xl bg-cyber-black border border-cyber-blue/20 flex items-center justify-center text-sm sm:text-lg lg:text-2xl font-cyber font-black text-cyber-blue text-glow-cyan">
                    {padZero(timeLeft.hours || 0)}
                  </div>
                  <span className="text-[8px] sm:text-[9px] text-gray-500 font-cyber font-bold mt-1.5 uppercase">Hrs</span>
                </div>

                <span className="text-sm sm:text-xl font-bold text-gray-600">:</span>

                {/* Minutes */}
                <div className="flex flex-col">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg lg:rounded-xl bg-cyber-black border border-cyber-blue/20 flex items-center justify-center text-sm sm:text-lg lg:text-2xl font-cyber font-black text-cyber-blue text-glow-cyan">
                    {padZero(timeLeft.minutes || 0)}
                  </div>
                  <span className="text-[8px] sm:text-[9px] text-gray-500 font-cyber font-bold mt-1.5 uppercase">Min</span>
                </div>

                <span className="text-sm sm:text-xl font-bold text-gray-600">:</span>

                {/* Seconds */}
                <div className="flex flex-col">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg lg:rounded-xl bg-cyber-black border border-cyber-pink/20 flex items-center justify-center text-sm sm:text-lg lg:text-2xl font-cyber font-black text-cyber-pink text-glow-pink">
                    {padZero(timeLeft.seconds || 0)}
                  </div>
                  <span className="text-[8px] sm:text-[9px] text-gray-500 font-cyber font-bold mt-1.5 uppercase">Sec</span>
                </div>

              </div>
            </div>

            {/* Side-by-side specs check */}
            <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6 text-[10px] sm:text-xs text-gray-400 font-mono text-center sm:text-left">
              <div className="flex items-center space-x-1.5">
                <ShieldCheck className="w-4 h-4 text-cyber-green flex-shrink-0" />
                <span>SECURE PAYMENT</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <Award className="w-4 h-4 text-cyber-blue flex-shrink-0" />
                <span>LIFETIME LIBRARY LICENSE</span>
              </div>
            </div>
          </div>

          {/* Glowing spinning light rings (behind background) */}
          <div className="absolute right-[-10%] top-[-10%] w-[400px] h-[400px] rounded-full border border-dashed border-cyber-pink/15 animate-spin-slow pointer-events-none z-0"></div>
          <div className="absolute right-[-20%] top-[-20%] w-[550px] h-[550px] rounded-full border border-dashed border-cyber-blue/15 animate-spin-slow pointer-events-none z-0" style={{ animationDirection: 'reverse' }}></div>
        </div>

      </div>
    </section>
  );
}
